/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { slicedToArray as _slicedToArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useEffect, useLayoutEffect } from 'react';
import setupGetInstanceId from '../tools/setupGetInstanceId.js';
import { canUseDOM } from './environment.js';
import { useIdPrefix } from './useIdPrefix.js';
var getId = setupGetInstanceId();
var useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
var serverHandoffCompleted = false;
/**
 * Generate a unique ID with an optional prefix prepended to it
 * @param {string} [prefix]
 * @returns {string}
 */

function useId() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'id';
  var _prefix = useIdPrefix();
  var _useState = useState(function () {
      if (serverHandoffCompleted) {
        return "".concat(_prefix ? "".concat(_prefix, "-") : "").concat(prefix, "-").concat(getId());
      }
      return null;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    id = _useState2[0],
    setId = _useState2[1];
  useIsomorphicLayoutEffect(function () {
    if (id === null) {
      setId("".concat(_prefix ? "".concat(_prefix, "-") : "").concat(prefix, "-").concat(getId()));
    }
  }, [getId]);
  useEffect(function () {
    if (serverHandoffCompleted === false) {
      serverHandoffCompleted = true;
    }
  }, []);
  return id;
}
/**
 * Generate a unique id if a given `id` is not provided
 * @param {string} id
 * @returns {string}
 */

function useFallbackId(id) {
  var fallback = useId();
  return id !== null && id !== void 0 ? id : fallback;
}
export { useFallbackId, useId };